import React, { useEffect, useRef, useState } from "react";
import { frontendErrorsFormat } from "@/features/observability/components/frontend-errors-format";
import { platformErrorsFormat } from "@/features/observability/platform-errors/components/platform-errors-format";
import type { PlatformErrorsEventsResource } from "@/features/observability/platform-errors/hooks/use-platform-errors";
import { UNREGISTERED_APP, type PlatformErrorEvent, type PlatformErrorGroup } from "@/features/observability/platform-errors/types/platform-errors.types";

type Props = {
    group: PlatformErrorGroup;
    resource: PlatformErrorsEventsResource;
    onClose: () => void;
    onMoveWindow: () => void;
};

const frameText = (asset: string | null, line: number | null, column: number | null): string => {
    if (asset === null) return "";
    const position = [line, column].filter((value): value is number => value !== null).join(":");
    return position === "" ? asset : `${asset}:${position}`;
};

function EventItem({ event }: { event: PlatformErrorEvent }) {
    const [open, setOpen] = useState(false);
    const severity = platformErrorsFormat.severityStyle(event.severity);
    const frames = event.frames === null ? [] : event.frames;
    const code = event.errorCode !== null ? event.errorCode : event.errorClass;

    return (
        <li className="fe-event" data-testid="pe-event">
            <div className="fe-event__hd">
                <span className="fe-event__time">{frontendErrorsFormat.dateTimeSeconds(event.effectiveOccurredAt)}</span>
                <span className="fe-chip">
                    <span className="fe-swatch" style={{ background: severity.color }} />
                    {severity.label}
                </span>
                <span className="fe-muted">{code === null ? "—" : code}</span>
                <span className="fe-code-chip">{event.httpStatus === null ? "sin HTTP" : `HTTP ${event.httpStatus}`}</span>
            </div>
            <pre className="fe-event__msg">{event.message === null ? "Sin mensaje" : event.message}</pre>
            <div className="fe-event__meta">
                <div>Servicio <span>{event.service === null ? "—" : event.service}</span></div>
                <div>Recurso <span>{event.resourceType === null ? "—" : event.resourceType}</span></div>
                <div>Recibido <span>{frontendErrorsFormat.nullableDateTime(event.receivedAt)}</span></div>
                <div>Fingerprint <span>{frontendErrorsFormat.short(event.fingerprint)}</span></div>
            </div>
            {frames.length > 0 ? (
                <>
                    <button type="button" className="fe-link" aria-expanded={open} onClick={() => setOpen(!open)}>
                        <span className="material-symbols-outlined" aria-hidden="true">{open ? "expand_more" : "chevron_right"}</span>
                        {`Stack · ${frames.length} frames`}
                    </button>
                    {open ? (
                        <ol className="fe-event__stack">
                            {frames.map((frame, index) => (
                                <li key={`${index}-${frame.functionName === null ? "" : frame.functionName}`}>
                                    <b>{frame.functionName === null ? "(anónima)" : frame.functionName}</b>{" "}
                                    <span className="fe-muted">{frameText(frame.asset, frame.line, frame.column)}</span>
                                </li>
                            ))}
                        </ol>
                    ) : null}
                </>
            ) : null}
        </li>
    );
}

const statusMessage = (resource: PlatformErrorsEventsResource): string | null => {
    if (resource.status === "loading" || resource.status === "idle") return "Cargando eventos…";
    // Only a failed first page replaces the list; a failed «Cargar más» keeps the pages shown and speaks in the footer.
    if (resource.status === "error" && platformErrorsFormat.isOutsideRetention(resource.error)) return `${platformErrorsFormat.OUTSIDE_RETENTION_TEXT}.`;
    if (resource.status === "forbidden") return "Tu usuario no tiene acceso a los errores de la plataforma.";
    if (resource.status === "unavailable") return "Fuente no disponible: no se pueden mostrar los eventos ahora.";
    if (resource.status === "error") return "No se pudieron cargar los eventos.";
    return null;
};

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function PlatformErrorsEventsDrawer({ group, resource, onClose, onMoveWindow }: Props) {
    const panelRef = useRef<HTMLElement>(null);
    const closeRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        const onKey = (event: KeyboardEvent): void => {
            if (event.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);

    // WCAG 2.4.3: focus moves into the dialog on open and back to whatever opened it (the group row) on close.
    useEffect(() => {
        const opener = document.activeElement;
        closeRef.current?.focus();
        return () => {
            if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
        };
    }, []);

    const trapTab = (event: React.KeyboardEvent<HTMLElement>): void => {
        if (event.key !== "Tab" || panelRef.current === null) return;
        const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (event.shiftKey === false && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    };

    const severity = platformErrorsFormat.severityStyle(group.severity);
    const message = statusMessage(resource);
    const events = resource.events;
    const outsideRetention = platformErrorsFormat.isOutsideRetention(resource.error);
    const retryable = outsideRetention === false && (resource.status === "unavailable" || resource.status === "error");

    return (
        <div className="fe-drawer">
            <div className="fe-drawer__scrim" onClick={onClose} aria-hidden="true" />
            <aside ref={panelRef} className="fe-drawer__panel" role="dialog" aria-modal="true" aria-label="Últimos eventos" onKeyDown={trapTab}>
                <div className="fe-drawer__hd">
                    <div className="fe-drawer__top">
                        <div className="fe-muted">{`Últimos eventos · fingerprint ${frontendErrorsFormat.short(group.fingerprint)}`}</div>
                        <button ref={closeRef} type="button" className="btn btn--ghost btn--icon" aria-label="Cerrar" onClick={onClose}>
                            <span className="material-symbols-outlined" aria-hidden="true">close</span>
                        </button>
                    </div>
                    <div className="fe-drawer__title">
                        <span className="fe-chip">
                            <span className="fe-swatch" style={{ background: severity.color }} />
                            {severity.label}
                        </span>
                        <span className="fe-drawer__code">{group.errorCode === null ? "Sin código" : group.errorCode}</span>
                        {group.errorClass === null ? null : <span className="fe-muted">{group.errorClass}</span>}
                    </div>
                    <div className="fe-muted">
                        {`${group.service === null ? "—" : group.service}${group.app === UNREGISTERED_APP ? " (no registrado)" : ""} · ${frontendErrorsFormat.count(group.count)} ocurrencias`}
                    </div>
                </div>
                {resource.status === "degraded" ? (
                    <div className="fe-notice fe-notice--warn fe-drawer__notice">
                        <span className="material-symbols-outlined" aria-hidden="true">warning</span>
                        Datos parciales: puede haber eventos que no se muestran
                    </div>
                ) : null}
                {message === null && resource.stale ? (
                    <div className="fe-notice fe-notice--warn fe-drawer__notice" role="status">
                        <span className="material-symbols-outlined" aria-hidden="true">history</span>
                        Datos obsoletos: la ingesta no confirma actividad reciente y puede haber eventos que aún no se ven
                    </div>
                ) : null}
                <div className="fe-drawer__body">
                    {message !== null ? (
                        <div className="fe-drawer__state">
                            {message}
                            {retryable ? <button type="button" className="btn btn--sm" onClick={resource.refresh}>Reintentar</button> : null}
                            {outsideRetention ? <button type="button" className="btn btn--sm" onClick={onMoveWindow}>Actualizar rango</button> : null}
                        </div>
                    ) : null}
                    {message === null && events !== null && events.length === 0 && resource.stale === false ? (
                        <div className="fe-drawer__state">No hay eventos de este grupo en el periodo.</div>
                    ) : null}
                    {message === null && events !== null && events.length > 0 ? (
                        <ul className="fe-events">
                            {events.map((event) => <EventItem key={event.receiptId} event={event} />)}
                        </ul>
                    ) : null}
                </div>
                <div className="fe-drawer__ft">
                    <span>{events === null ? "" : `Mostrando ${frontendErrorsFormat.count(events.length)} eventos`}</span>
                    {resource.error !== null && message === null ? (
                        outsideRetention ? (
                            <span className="fe-drawer__error" role="status">
                                {`${platformErrorsFormat.OUTSIDE_RETENTION_TEXT}.`}
                                <button type="button" className="btn btn--sm" onClick={onMoveWindow}>Actualizar rango</button>
                            </span>
                        ) : <span className="fe-drawer__error" role="status">No se pudieron cargar más eventos.</span>
                    ) : null}
                    {/* The same cursor would only repeat the retention 400: moving the window is the way on. */}
                    {resource.hasMore && outsideRetention === false ? (
                        <button type="button" className="btn btn--sm" disabled={resource.loadingMore} onClick={resource.loadMore}>
                            {resource.loadingMore ? "Cargando…" : "Cargar más"}
                        </button>
                    ) : null}
                </div>
            </aside>
        </div>
    );
}
